<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { almConfig, ALM_SYNC_STATUS } from "@/almConfig";

import BaseButton from "./BaseButton.vue";
import BaseDropdown from "./BaseDropdown.vue";
import BaseSpinner from "./BaseSpinner.vue";
import StatusDot from "./StatusDot.vue";
import SvgIcon from "./SvgIcon.vue";

const props = defineProps({
  status: {
    type: String,
    required: true,
    validator: (v) => ["running", "stopped", "error", "starting"].includes(v),
  },
  almType: {
    type: String,
    required: true,
    validator: (v) => ["jira", "ado", "ac"].includes(v),
  },
  // When false, the dropdown still opens but sync actions are disabled and the
  // explanatory message is hidden — for users without edit rights on the session.
  editable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["stop", "restart", "force-run", "force-stop", "fix-config", "open"]);

const provider = computed(() => almConfig(props.almType));

// Optimistic transient shown right after an action click. Cleared once the real
// status prop changes, or after a fallback timeout so an action that fails
// without a status change can't freeze the pill on a dropdown-less "Syncing..".
const pending = ref(null); // "stopping" | "starting" | null
let pendingTimer = null;
function clearPending() {
  pending.value = null;
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
}
watch(() => props.status, clearPending);

const isSyncing = computed(() => props.status === "starting" || pending.value !== null);
const syncingLabel = computed(() =>
  pending.value === "stopping" ? "Stopping Sync.." : "Syncing..",
);

const view = computed(() => {
  const s = props.status;
  return {
    label: ALM_SYNC_STATUS[s]?.label,
    dotState: ALM_SYNC_STATUS[s]?.dot,
    actionLabel: s === "running" ? "Stop Sync" : "Restart Sync",
    actionColor: s === "running" ? "destructive" : "primary",
    messageType: s === "error" ? "warning" : "info",
  };
});

function onAction() {
  const isRunning = props.status === "running";
  pending.value = isRunning ? "stopping" : "starting";
  emit(isRunning ? "stop" : "restart");
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(clearPending, 10000);
}

// Alt-revealed power-user actions (Force Run / Force Stop), mirroring the
// previous status cell behavior. Tracked on the window so it works even while
// the pointer is over the teleported dropdown panel.
const altHeld = ref(false);
function onAltKey(event) {
  altHeld.value = event.altKey;
}
onMounted(() => {
  window.addEventListener("keydown", onAltKey);
  window.addEventListener("keyup", onAltKey);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onAltKey);
  window.removeEventListener("keyup", onAltKey);
  if (pendingTimer) clearTimeout(pendingTimer);
});
</script>

<template>
  <div class="alm-sync-control">
    <template v-if="isSyncing">
      <span class="pill syncing">
        <BaseSpinner size="16" />
        <span class="label">{{ syncingLabel }}</span>
      </span>
      <BaseButton
        v-if="status === 'starting' && altHeld && editable"
        variant="text"
        color="grey"
        size="small"
        @click="emit('force-stop')"
      >
        Force Stop
      </BaseButton>
    </template>

    <BaseDropdown v-else align="left" @open="emit('open')">
      <template #trigger="{ isOpen }">
        <span class="pill" :class="{ open: isOpen }">
          <StatusDot :state="view.dotState" />
          <span class="label">{{ view.label }}</span>
          <SvgIcon
            :name="isOpen ? 'system/chevron-up' : 'system/chevron-down'"
            width="16"
            height="16"
            class="chevron"
          />
        </span>
      </template>

      <div class="alm-sync-panel">
        <div class="panel-body">
          <div class="panel-header">
            <SvgIcon :name="provider.icon" width="18" height="18" aria-hidden="true" />
            <span class="provider-name">{{ provider.displayName }} Connection</span>
          </div>

          <BaseButton full-width :color="view.actionColor" :disabled="!editable" @click="onAction">
            {{ view.actionLabel }}
          </BaseButton>

          <BaseButton
            v-if="status === 'stopped' && altHeld && editable"
            variant="text"
            color="grey"
            size="small"
            class="force"
            @click="emit('force-run')"
          >
            Force Run
          </BaseButton>
        </div>

        <div v-if="editable" class="panel-message" :class="view.messageType">
          <SvgIcon
            :name="view.messageType === 'warning' ? 'message/warning' : 'message/info'"
            width="16"
            height="16"
            class="message-icon"
          />
          <p class="message-text">
            <template v-if="status === 'error'">
              The sync stopped due to a connection or configuration issue. Restart sync or
              <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
              <button class="config-link" @click="emit('fix-config')">
                check the ALM configuration.
              </button>
            </template>
            <template v-else-if="status === 'stopped'">
              This session is no longer syncing with {{ provider.displayName }}. Resume sync at any
              time.
            </template>
            <template v-else>
              Stopping the sync will disconnect this session from {{ provider.displayName }} and
              stop future updates. Your current data will remain available.
            </template>
          </p>
        </div>
      </div>
    </BaseDropdown>
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.alm-sync-control {
  width: max-content;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid c.$border-light;
  border-radius: 100px;
  background-color: c.$background-ui;
  white-space: nowrap;

  .label {
    @include typography.small("semi-bold");

    color: c.$text;
  }

  .chevron {
    color: c.$text;
  }

  &:not(.syncing):hover,
  &.open {
    background-color: c.$background-grey-light;
    cursor: pointer;
  }

  &.syncing {
    border-color: transparent;
    background-color: transparent;
    padding-left: 0;

    .label {
      color: c.$text-subtle;
    }
  }
}

.alm-sync-panel {
  // Cancel BaseDropdown's .dropdown-content 4px padding so the message section
  // can run full-bleed to the card edges (matching the Figma menu). overflow
  // + radius clip the message's bottom corners to the rounded card.
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 300px;
  margin: -4px;
  border-radius: 8px;
  overflow: hidden;
}

.panel-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 8px;

  .provider-name {
    @include typography.medium("bold");

    color: c.$text;
  }
}

.force {
  align-self: flex-start;
}

.panel-message {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 12px;

  &.info {
    background-color: c.$background-grey-light;
  }

  &.warning {
    background-color: c.$orange-light;
  }

  .message-icon {
    flex-shrink: 0;
  }

  &.info .message-icon {
    color: c.$text;
  }

  &.warning .message-icon {
    color: c.$brand-warning;
  }

  .message-text {
    @include typography.small("semi-bold");

    margin: 0;
    color: c.$text;
  }
}

.config-link {
  font: inherit;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: c.$main;
  text-decoration: underline;
}
</style>
